import {
  AlertTriangle,
  Download,
  Edit,
  Loader2,
  Mail,
  PlayCircle,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { openPaymentModal } from "~/functions/emergepayfn";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavLink } from "react-router-dom";
import api from "~/services/api";
import { toast } from "react-toastify";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { Form } from "@unform/web";
import { AlertContext } from "~/App";

export const InputContext = createContext({});

function EnrollmentProcess({
  enrollment = null,
  student_id = null,
  setLoading = () => null,
  loading = false,
  handleStartProcess = null,
  disabled = false,
}) {
  const process = `Enrollment Process`;
  const routine = `enrollment-process`;
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [globalPaymentMethod, setGlobalPaymentMethod] = useState(null);
  const [globalInvoices, setGlobalInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState(false);
  const [discountUpdates, setDiscountUpdates] = useState([]);
  const [updatingInvoices, setUpdatingInvoices] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState(enrollment?.paymentmethod_id || null);
  const [processingInvoiceId, setProcessingInvoiceId] = useState(null);
  const [cardData, setCardData] = useState({});
  const generalForm = useRef();
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(false);
  const { alertBox } = useContext(AlertContext);
  const isGlobal =
    enrollment?.contract?.processtypes?.name === "Non-F1" &&
    enrollment?.contract?.processsubstatuses?.name === "Global";
  const selectedInvoices = invoices.filter((invoice) =>
    selectedInvoiceIds.includes(invoice.id)
  );
  const selectedBalance = selectedInvoices.reduce(
    (total, invoice) => total + Number(invoice.balance || 0),
    0
  );
  const canSendSelectedInvoices =
    !loading && !disabled && !updatingInvoices && !invoicesLoading && !invoicesError && !!paymentMethodId && selectedInvoices.length > 0;

  function handleSendMail(type = "enrollment-process", sponsor_id = null) {
    setLoading(true);
    api
      .post(`/enrollments/send-form-mail`, {
        type: type ? type : routine,
        enrollment_id: enrollment.id,
        student_id,
        ...(sponsor_id && { sponsor_id }),
      })
      .then(({ data }) => {
        setLoading(false);
        toast("E-mail has been sent!", { autoClose: 1000 });
      })
      .catch((error) => {
        setLoading(false);
        toast.error("Form mail failed!");
      });
  }
  function applyInvoiceData(data) {
    if (isGlobal) setGlobalInvoices(data?.receivables || []);
    else setInvoices(data?.receivables || []);
    setDiscountUpdates(data?.discount_updates || []);
  }

  async function loadData() {
    setInvoicesLoading(true);
    setInvoicesError(false);
    const methodsRequest = api.get(`/paymentmethods`).then(({ data }) => {
      const options = data.rows.filter((method) => method.is_available_at_enrollment &&
        Number(method.filial_id) === Number(enrollment.filial_id))
        .map((method) => ({ value: method.id, label: method.description.slice(0, 20) }));
      setPaymentMethods(options);
      setPaymentMethodId((current) => options.some((option) => option.value === current) ? current : null);
    }).catch(() => toast.error("Failed to load payment methods."));
    const invoicesRequest = (async () => {
      try {
        if (enrollment?.id) {
          const { data } = await api.get(`/enrollments/${enrollment.id}/invoices`);
          applyInvoiceData(data);
        }
      } catch (err) {
        setInvoicesError(true);
        setDiscountUpdates([]);
        toast.error("Failed to load invoices.");
      } finally {
        setInvoicesLoading(false);
      }
    })();
    await Promise.all([methodsRequest, invoicesRequest]);
  }

  async function handleUpdateInvoices() {
    if (disabled || updatingInvoices || invoicesLoading || invoicesError || !discountUpdates.length) return;
    setUpdatingInvoices(true);
    setLoading(true);
    try {
      const { data } = await api.post(`/enrollments/${enrollment.id}/invoices/update-discounts`);
      applyInvoiceData(data);
      setSelectedInvoiceIds([]);
      toast.success("Invoices updated. Send a new payment link if needed.");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update invoices.");
    } finally {
      setUpdatingInvoices(false);
      setLoading(false);
    }
  }

  function discountNotice() {
    if (invoicesLoading || invoicesError || !discountUpdates.length) return null;
    return (
      <div className="flex flex-wrap items-center justify-end gap-3 text-left" style={{ marginLeft: "auto" }} role="status">
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-mila_orange" aria-hidden="true" />
          <span className="text-xs font-semibold text-slate-700">
            Discounts not reflected in {discountUpdates.length} {discountUpdates.length === 1 ? "invoice" : "invoices"}. Update now?
          </span>
        </div>
        <button type="button" onClick={handleUpdateInvoices}
          disabled={disabled || updatingInvoices}
          title={disabled ? "This enrollment is inactive." : "Apply the saved discounts to these invoices"}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-mila_orange bg-mila_orange px-4 py-2 text-xs font-bold text-white cursor-pointer hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mila_orange focus-visible:ring-offset-2 disabled:cursor-not-allowed">
          {updatingInvoices ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />}
          {updatingInvoices ? "Updating..." : "Update invoices"}
        </button>
      </div>
    );
  }
  useEffect(() => {
    loadData();
  }, []);

  async function handleGlobalSendInvoice(receivable) {
    if (!globalPaymentMethod) {
      toast.error("Please select a Payment Method.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/prospect_payments/payment_link`, {
        issuer_id: enrollment.students?.issuer?.id,
        registrationFee_id:
          receivable.type_detail === "Registration fee" ? receivable.id : null,
        tuitionFee_id:
          receivable.type_detail === "Tuition fee" ? receivable.id : null,
        paymentmethod_id: globalPaymentMethod,
        enrollment_id: enrollment.id,
        student_id,
      });
      toast.success("Invoice link sent to student!");
    } catch (err) {
      toast.error("Failed to send invoice link.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGlobalProcessPayment(receivable) {
    try {
      await openPaymentModal({
        receivable: {
          id: receivable.id,
          total: parseFloat(receivable.balance || 0),
          memo: receivable.type_detail,
        },
      });
      setGlobalInvoices((prev) =>
        prev.map((r) =>
          r.id === receivable.id ? { ...r, status: "Paid" } : r
        )
      );
    } catch (err) {
      toast.error("Failed to open payment modal.");
    }
  }

  async function handleGenerateFees() {
    setLoading(true);
    try {
      await api.post(`/prospect_payments/generateFees`, {
        filial_id: enrollment.filial_id,
        enrollment_id: enrollment.id,
        student_id,
      });
      const { data } = await api.get(`/enrollments/${enrollment.id}/invoices`);
      applyInvoiceData(data);
      setSelectedInvoiceIds([]);
      toast.success("Invoices created successfully!");
    } catch (error) {
      console.log(error);
      toast.error("Failed to create invoices.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSendSelectedInvoices() {
    if (!paymentMethodId || selectedInvoiceIds.length === 0) {
      toast.error("Select a payment method and at least one invoice.");
      return;
    }
    const selected = invoices.filter((invoice) => selectedInvoiceIds.includes(invoice.id));
    if (new Set(selected.map((invoice) => invoice.type_detail)).size !== selected.length) {
      toast.error("Select at most one invoice of each type.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/prospect_payments/payment_link`, {
        issuer_id: enrollment.students?.issuer?.id,
        registrationFee_id: selected.find((invoice) => invoice.type_detail === "Registration fee")?.id || null,
        tuitionFee_id: selected.find((invoice) => invoice.type_detail === "Tuition fee")?.id || null,
        paymentmethod_id: paymentMethodId,
        enrollment_id: enrollment.id,
        student_id,
      });
      setSelectedInvoiceIds([]);
      const { data } = await api.get(`/enrollments/${enrollment.id}/invoices`);
      applyInvoiceData(data);
      toast.success("Payment link sent to student!");
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.error || "Failed to send payment link.");
    } finally {
      setLoading(false);
    }
  }

  function handlePDF() {
    api
      .get(`/pdf/enrollment/${enrollment.id}`, {
        responseType: "blob",
      })
      .then((res) => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        saveAs(pdfBlob, `enrollment_${enrollment.id}.pdf`);
      });
  }

  function handlePDF2() {
    const endpoint = isGlobal
      ? `/pdf/new-enrollment-global/${enrollment.id}`
      : `/pdf/new-enrollment/${enrollment.id}`;
    api
      .get(endpoint, {
        responseType: "blob",
      })
      .then((res) => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        saveAs(pdfBlob, `enrollment_${enrollment.id}_new.pdf`);
      });
  }

  async function handleRemoveSignature() {
    setLoading(true);
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to remove this student's signature? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: () => {
            setLoading(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(
                `/enrollment-student-signature/${enrollment.id}`
              );
              toast("Student Signature removed!", { autoClose: 1000 });
              setLoading(false);
              enrollment.student_signature = null;
            } catch (err) {
              setLoading(false);
              console.log(err);
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  if (enrollment.form_step === "not-started") {
    return (
      <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
        <div className="flex w-96 flex-col items-center justify-center text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
          {loading ? (
            <div className="w-full bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-between text-xs gap-1">
              <strong>Please wait...</strong>
              <Loader2 className="animate-spin" size={14} />
            </div>
          ) : (
            <button
              type="button"
              disabled={loading || disabled}
              onClick={() => handleStartProcess(routine)}
              className={`w-full rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-between text-xs gap-1 ${
                disabled
                  ? "bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50"
                  : "bg-green-300 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white"
              }`}
            >
              <strong>Start {process} Process</strong>
              <PlayCircle size={18} />
            </button>
          )}
          <p className="text-xs text-gray-500 text-left px-2 border-t pt-4">
            This action will send an email to the student with a link to
            complete the {process} form.
          </p>
        </div>
      </div>
    );
  }

  return (
    <InputContext.Provider
      value={{
        id: null,
        generalForm,
        successfullyUpdated,
        setSuccessfullyUpdated,
        fullscreen: false,
        setFullscreen: () => null,
        handleCloseForm: () => null,
      }}
    >
      <Form ref={generalForm} onSubmit={handleGenerateFees} className="w-full">
        <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
          <div className="relative flex w-full flex-col items-start justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
            <h2 className="text-md font-bold w-full text-left border-b border-gray-200 pb-2">
              Enrollment Process
              {enrollment.student_signature ? (
                <span className="text-green-500"> - Signed</span>
              ) : (
                <span className="text-red-500"> - Not signed yet</span>
              )}
            </h2>
            <div className="relative flex w-full flex-row items-center justify-start text-center gap-4">
              {disabled ? (
                <span className="bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50 rounded-md py-4 px-4 my-2 h-6 flex flex-row items-center justify-start text-xs gap-2">
                  <Edit size={14} />
                  <strong>Access enrollment form</strong>
                </span>
              ) : (
                <NavLink
                  to={`/fill-form/Enrollment?crypt=${enrollment.id}`}
                  target="_blank"
                  className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                >
                  <Edit size={14} />
                  <strong>Access enrollment form</strong>
                </NavLink>
              )}
              {!enrollment.student_signature ? (
                <>
                  <button
                    type="button"
                    disabled={loading || disabled}
                    onClick={() => handleSendMail()}
                    className={`rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 ${
                      disabled
                        ? "bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50"
                        : "bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white cursor-pointer"
                    }`}
                  >
                    <Mail size={14} />
                    <strong>Send form link to student</strong>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handlePDF2}
                    className={`bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 cursor-pointer`}
                  >
                    <Download size={14} />
                    <strong>Download PDF - Novo</strong>
                  </button>

                  <button
                    type="button"
                    disabled={loading || disabled}
                    onClick={handleRemoveSignature}
                    className={`rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 ${
                      disabled
                        ? "bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50"
                        : "bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white cursor-pointer"
                    }`}
                  >
                    <Trash2 size={14} />
                    <strong>Remove Student Signature</strong>
                  </button>
                </>
              )}
            </div>
          </div>
          {enrollment.enrollmentsponsors.length > 0 &&
            enrollment.enrollmentsponsors.map((sponsor, index) => {
              if (sponsor.name) {
                return (
                  <div className="relative flex w-full flex-col items-start justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
                    <h2 className="text-md font-bold w-full text-left border-b border-gray-200 pb-2">
                      Sponsor #{index + 1} - {sponsor.name}{" "}
                      {sponsor.signature ? (
                        <span className="text-green-500"> - Signed</span>
                      ) : (
                        <span className="text-red-500"> - Not signed yet</span>
                      )}
                    </h2>
                    <div className="relative flex w-full flex-row items-center justify-start text-center gap-4">
                      {disabled ? (
                        <span className="bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50 rounded-md py-4 px-4 my-2 h-6 flex flex-row items-center justify-start text-xs gap-2">
                          <Edit size={14} />
                          <strong>Access sponsor form</strong>
                        </span>
                      ) : (
                        <NavLink
                          to={`/fill-form/Sponsor?crypt=${sponsor.id}`}
                          target="_blank"
                          className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                        >
                          <Edit size={14} />
                          <strong>Access sponsor form</strong>
                        </NavLink>
                      )}
                      {!sponsor.signature ? (
                        <>
                          <button
                            type="button"
                            disabled={loading || disabled}
                            onClick={() => handleSendMail("sponsor-signature", sponsor.id)}
                            className={`rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 ${
                              disabled
                                ? "bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50"
                                : "bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white"
                            }`}
                          >
                            <Mail size={14} />
                            <strong>Send form link to sponsor</strong>
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              }
            })}
          {isGlobal ? (
            <div className="relative flex w-full flex-col items-start justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
              <div className="flex w-full flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2 text-left">
                <h2 className="text-md font-bold">Invoices</h2>
                {discountNotice()}
              </div>
              <div className="relative flex w-full flex-row items-center justify-start gap-4 mb-2">
                <SelectPopover
                  name="global_paymentmethod_id"
                  title="Payment Method"
                  isSearchable
                  grow
                  required
                  disabled={disabled}
                  options={paymentMethods}
                  onChange={(el) => setGlobalPaymentMethod(el?.value || null)}
                  InputContext={InputContext}
                />
              </div>
              {globalInvoices.length === 0 ? (
                <p className="text-xs text-gray-400">No invoices found.</p>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left px-2 py-1">Type</th>
                      <th className="text-left px-2 py-1">Due Date</th>
                      <th className="text-right px-2 py-1">Total</th>
                      <th className="text-left px-2 py-1">Status</th>
                      <th className="text-right px-2 py-1">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalInvoices.map((inv) => (
                      <tr key={inv.id} className="border-b even:bg-gray-50">
                        <td className="px-2 py-1">{inv.type_detail}</td>
                        <td className="px-2 py-1">{inv.due_date || "-"}</td>
                        <td className="px-2 py-1 text-right">
                          ${parseFloat(inv.total || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-1">
                          <span
                            className={
                              inv.status === "Paid"
                                ? "text-green-600 font-semibold"
                                : "text-red-500"
                            }
                          >
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 flex flex-row gap-1 justify-end">
                          {inv.status !== "Paid" && (
                            <>
                              <button
                                type="button"
                                disabled={loading || disabled}
                                onClick={() => handleGlobalSendInvoice(inv)}
                                className={`rounded px-2 py-1 flex flex-row items-center gap-1 text-xs ${
                                  disabled
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed opacity-50"
                                    : "bg-slate-300 text-slate-600 border border-slate-400 hover:bg-slate-400 hover:text-white"
                                }`}
                              >
                                <Mail size={12} />
                                <strong>Send</strong>
                              </button>
                              <button
                                type="button"
                                disabled={loading || disabled || updatingInvoices}
                                onClick={() => handleGlobalProcessPayment(inv)}
                                className={`rounded px-2 py-1 flex flex-row items-center gap-1 text-xs ${
                                  disabled
                                    ? "bg-slate-200 text-slate-400 cursor-not-allowed opacity-50"
                                    : "bg-green-200 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white"
                                }`}
                              >
                                <PlayCircle size={12} />
                                <strong>Process Payment</strong>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
          <div className="relative flex w-full flex-col items-start justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
            <div className="flex w-full flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2 text-left">
              <h2 className="text-md font-bold">Payment invoices</h2>
              {discountNotice()}
            </div>
            {invoicesLoading ? (
              <p className="text-xs text-gray-400">Loading invoices...</p>
            ) : invoicesError ? (
              <button
                type="button"
                onClick={loadData}
                className="rounded border border-slate-400 bg-slate-300 px-3 py-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-400 hover:text-white"
              >
                Retry loading invoices
              </button>
            ) : invoices.length === 0 ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-xs text-gray-400">No invoices found.</p>
                <button
                  type="button"
                  onClick={handleGenerateFees}
                  disabled={loading || disabled}
                  className="rounded border border-slate-400 bg-slate-300 px-3 py-2 text-xs text-slate-700 transition-colors hover:bg-slate-400 hover:text-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-slate-300 disabled:hover:text-slate-700"
                >
                  Create invoices
                </button>
              </div>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-2 py-1">Select</th>
                    <th className="text-left px-2 py-1">Invoice</th>
                    <th className="text-left px-2 py-1">Type</th>
                    <th className="text-left px-2 py-1">Due Date</th>
                    <th className="text-right px-2 py-1">Balance</th>
                    <th className="text-left px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const payable = ["Pending", "Partial Paid"].includes(invoice.status) && Number(invoice.balance) > 0;
                    const anotherInvoiceOfTypeSelected = selectedInvoices.some(
                      (selected) => selected.type_detail === invoice.type_detail && selected.id !== invoice.id
                    );
                    return (
                      <tr key={invoice.id} className="border-b even:bg-gray-50 hover:bg-slate-100">
                        <td className="px-2 py-1">
                          <input
                            type="checkbox"
                            checked={selectedInvoiceIds.includes(invoice.id)}
                            disabled={!payable || anotherInvoiceOfTypeSelected || loading || disabled}
                            aria-label={`Select invoice I${String(invoice.invoice_number).padStart(6, "0")}`}
                            className="cursor-pointer disabled:cursor-not-allowed"
                            onChange={(event) => setSelectedInvoiceIds((current) =>
                              event.target.checked
                                ? [...current, invoice.id]
                                : current.filter((id) => id !== invoice.id)
                            )}
                          />
                        </td>
                        <td className="px-2 py-1">I{String(invoice.invoice_number).padStart(6, "0")}</td>
                        <td className="px-2 py-1">{invoice.type_detail}</td>
                        <td className="px-2 py-1">{invoice.due_date || "-"}</td>
                        <td className="px-2 py-1 text-right">${Number(invoice.balance || 0).toFixed(2)}</td>
                        <td className="px-2 py-1">{invoice.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {selectedInvoices.length > 0 && (
              <p className="text-xs text-slate-600">
                {selectedInvoices.length === 2
                  ? "2 invoices selected — one payment link for the combined balance: "
                  : "1 invoice selected — payment link amount: "}
                <strong>${selectedBalance.toFixed(2)}</strong>
              </p>
            )}
            {invoices.some((invoice) => ["Pending", "Partial Paid"].includes(invoice.status) && Number(invoice.balance) > 0) && (
              <div className="relative flex w-full flex-row items-center justify-start text-center gap-4">
                <SelectPopover
                  name="paymentmethod_id"
                  title="Payment Method"
                  isSearchable
                  grow
                  required
                  disabled={disabled}
                  defaultValue={
                    enrollment.paymentmethod_id
                      ? paymentMethods.find(
                          (paymentMethod) =>
                            paymentMethod.value === enrollment.paymentmethod_id
                        )
                      : null
                  }
                  options={paymentMethods}
                  onChange={(option) => setPaymentMethodId(option?.value || null)}
                  InputContext={InputContext}
                />
                <button
                  type="button"
                  onClick={handleSendSelectedInvoices}
                  disabled={!canSendSelectedInvoices}
                  className={`rounded-md py-4 px-4 mb-2 mt-5 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 ${
                    !canSendSelectedInvoices
                      ? "bg-slate-200 text-slate-400 border border-slate-300 cursor-not-allowed opacity-50"
                      : "bg-slate-300 text-slate-700 border border-slate-400 cursor-pointer transition-colors hover:bg-slate-400 hover:text-white"
                  }`}
                >
                  <Send size={14} />
                  <strong>{selectedInvoices.length === 2 ? "Send one link for both" : "Send payment link"}</strong>
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </Form>
    </InputContext.Provider>
  );
}

export default EnrollmentProcess;
